import { useCallback, useEffect, useRef } from 'react';
import { env } from '@/utils/env';
import { isMockChargeSessionsActive } from '../dev/mock-charge-sessions';
import { useChargeSessionsStore } from '../store/charge-sessions-store';
import {
  inboundMessageSchema,
  type OutboundMessage,
  type SessionLevel,
  type Viewport,
} from '../types/charge-session.types';

/** Debounce window for outbound `monta:viewport` posts (ms). */
export const VIEWPORT_DEBOUNCE_MS = 300;

/**
 * Bridges the charge-session map iframe to its Hub studio-viewer parent over
 * `postMessage` (BIZOPS-1588, contract v2 in `.context/charge-sessions-spec.md`
 * §15 — server-driven viewport clustering).
 *
 * Inbound (DOWN): validates `event.origin` against `env.HUB_ALLOWED_ORIGINS`
 * BEFORE parsing, then `.safeParse`s into the discriminated union and routes to
 * the store. Anything from a non-allowlisted origin, or any malformed/unrelated
 * payload, is dropped silently. `monta:charge-sessions` carries the H3 clusters
 * + leaves for the last reported viewport.
 *
 * Outbound (UP):
 * - `monta:ready` is posted on mount. Because we have not yet received a
 *   message, the validated parent origin is unknown, so the handshake is posted
 *   once per allowlisted origin (each as an explicit `targetOrigin`) rather than
 *   with `'*'`.
 * - `monta:viewport` is posted (debounced ~{@link VIEWPORT_DEBOUNCE_MS}ms) via
 *   `sendViewport` whenever the map settles on a new bounds/zoom; the parent
 *   re-queries the gateway for that viewport. The map host also calls it once
 *   after mount so the first viewport goes out right after `monta:ready`.
 * - `requestSessionDetail` posts `monta:session-detail-request` on LEAF click
 *   only (clusters zoom instead), pinned to the most recently validated inbound
 *   origin; before any inbound message arrives it falls back to the allowlist.
 *
 * HARD ISOLATION: this hook performs ZERO network I/O. All data arrives via
 * postMessage only.
 */
export function useChargeSessionMessages() {
  const setClusters = useChargeSessionsStore((s) => s.setClusters);
  const setDetail = useChargeSessionsStore((s) => s.setDetail);
  const registerSendViewport = useChargeSessionsStore(
    (s) => s.registerSendViewport
  );

  // Origin of the most recently validated inbound message. Outbound messages
  // are pinned to this exact origin once known (never '*').
  const validatedParentOriginRef = useRef<string | null>(null);

  // Pending debounce timer for outbound viewport posts.
  const viewportTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const postToParent = useCallback((message: OutboundMessage) => {
    let parent = window.parent;
    if (!parent || parent === window) {
      // DEV-ONLY: when running standalone under the local mock (`?mock=1` in a
      // dev build), there is no real parent window — `window.parent` is
      // `window` itself. Post UP messages to our own window so the in-page
      // mock parent (mock-charge-sessions.ts) receives them. In production
      // (and any non-mock session) there is no embedding parent to talk to,
      // so we bail exactly as before — prod behavior is unchanged.
      if (!isMockChargeSessionsActive()) {
        return;
      }
      parent = window;
    }
    // Once we have a validated parent, pin to it exclusively.
    const knownOrigin = validatedParentOriginRef.current;
    if (knownOrigin) {
      parent.postMessage(message, knownOrigin);
      return;
    }
    // Bootstrap: no inbound message seen yet, so the real parent origin is
    // unknown. Post to each allowlisted origin explicitly — never '*'.
    for (const origin of env.HUB_ALLOWED_ORIGINS) {
      parent.postMessage(message, origin);
    }
  }, []);

  /**
   * Report the current map viewport to the parent (debounced). Called by the
   * map host on every map `idle`/`bounds_changed`. Coalesces a burst of
   * pan/zoom events into a single post ~{@link VIEWPORT_DEBOUNCE_MS}ms after the
   * map settles.
   */
  const sendViewport = useCallback(
    (viewport: Viewport) => {
      if (viewportTimerRef.current !== null) {
        clearTimeout(viewportTimerRef.current);
      }
      viewportTimerRef.current = setTimeout(() => {
        viewportTimerRef.current = null;
        postToParent({
          type: 'monta:viewport',
          top: viewport.top,
          bottom: viewport.bottom,
          left: viewport.left,
          right: viewport.right,
          zoom: viewport.zoom,
        });
      }, VIEWPORT_DEBOUNCE_MS);
    },
    [postToParent]
  );

  const requestSessionDetail = useCallback(
    (id: string, level: SessionLevel) => {
      postToParent({
        type: 'monta:session-detail-request',
        id: id,
        level: level,
      });
    },
    [postToParent]
  );

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // 1. Origin gate (exact Set match) — BEFORE any parsing.
      if (!env.HUB_ALLOWED_ORIGINS.has(event.origin)) {
        return;
      }

      // 2. Validate the payload shape; drop anything that doesn't match.
      const parsed = inboundMessageSchema.safeParse(event.data);
      if (!parsed.success) {
        return;
      }

      // 3. Remember the validated parent so outbound messages can pin to it.
      validatedParentOriginRef.current = event.origin;

      // 4. Route by discriminant.
      const message = parsed.data;
      if (message.type === 'monta:charge-sessions') {
        setClusters({
          clusters: message.clusters,
          leaves: message.leaves,
          zoom: message.zoom,
          resolution: message.resolution,
          generatedAt: message.generatedAt,
        });
        return;
      }
      // message.type === 'monta:session-detail'
      setDetail(message);
    };

    window.addEventListener('message', handleMessage);

    // Announce readiness so the parent starts pushing Tier-1 data for whatever
    // viewport we report next.
    postToParent({ type: 'monta:ready' });

    return () => {
      window.removeEventListener('message', handleMessage);
      if (viewportTimerRef.current !== null) {
        clearTimeout(viewportTimerRef.current);
        viewportTimerRef.current = null;
      }
    };
  }, [postToParent, setClusters, setDetail]);

  // Expose the debounced viewport reporter to the map host via the store, so the
  // bridge stays the single owner of the postMessage protocol while the map host
  // (which holds the map instance) owns reading bounds/zoom.
  useEffect(() => {
    registerSendViewport(sendViewport);
    return () => {
      registerSendViewport(null);
    };
  }, [registerSendViewport, sendViewport]);

  return {
    sendViewport: sendViewport,
    requestSessionDetail: requestSessionDetail,
  };
}
